import React, { useState } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { FaGraduationCap, FaChevronDown } from 'react-icons/fa';

const EducationContainer = styled.div`
  padding: 2rem;
  width: 100%;

  @media (max-width: 768px) {
    padding: 3rem 1rem;
  }
`;

const Content = styled.div`
  max-width: 1200px;
  width: 100%;
  margin: 0 auto;
`;

const SubTitle = styled(motion.p)`
  color: #666;
  letter-spacing: 2px;
  font-size: 0.8rem;
  margin-bottom: 0.5rem;
  text-transform: uppercase;
`;

const Title = styled(motion.h2)`
  font-size: 2rem;
  margin-bottom: 2rem;
  font-family: 'Playfair Display', serif;
  color: #333;

  @media (max-width: 768px) {
    font-size: 1.8rem;
    margin-bottom: 1.5rem;
  }
`;

const Timeline = styled.div`
  position: relative;
  max-width: 800px;
  margin: 0 auto;

  @media (max-width: 768px) {
    &::before {
      left: 20px;
    }
  }

  &::before {
    content: '';
    position: absolute;
    left: 50%;
    transform: translateX(-50%);
    width: 2px;
    height: 100%;
    background: #e0e0e0;
  }
`;

const TimelineItem = styled(motion.div)`
  margin-bottom: 2rem;
  position: relative;
  width: 100%;

  @media (max-width: 768px) {
    margin-bottom: 1.5rem;
  }
`;

const TimelineContent = styled.div`
  position: relative;
  width: calc(50% - 30px);
  padding: 1rem;
  background: #f8f9fa;
  border-radius: 10px;
  margin-left: ${props => props.align === 'right' ? 'calc(50% + 30px)' : '0'};

  @media (max-width: 768px) {
    width: calc(100% - 50px);
    margin-left: 50px;
    padding: 0.8rem;
  }

  &::before {
    content: '';
    position: absolute;
    top: 20px;
    ${props => props.align === 'right' ? 'left: -30px' : 'right: -30px'};
    width: 30px;
    height: 2px;
    background: #e0e0e0;

    @media (max-width: 768px) {
      left: -30px;
      right: auto;
    }
  }
`;

const TimelineIcon = styled.div`
  position: absolute;
  left: 50%;
  top: 20px;
  transform: translateX(-50%);
  width: 35px;
  height: 35px;
  background: #0066cc;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;

  @media (max-width: 768px) {
    left: 20px;
    transform: none;
    width: 30px;
    height: 30px;
  }
`;

const TimelineHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 0.5rem;
  cursor: pointer;
  gap: 0.5rem;
`;

const SchoolName = styled.h3`
  font-size: 0.9rem;
  color: #333;
  font-family: 'Playfair Display', serif;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 400px;

  @media (max-width: 768px) {
    font-size: 0.85rem;
    max-width: 200px;
  }
`;

const Year = styled.span`
  color: #666;
  font-size: 0.8rem;
  display: block;
  margin-top: 0.25rem;

  @media (max-width: 768px) {
    font-size: 0.75rem;
  }
`;

const Degree = styled.h4`
  color: #0066cc;
  font-size: 0.9rem;
  margin: 0.25rem 0;

  @media (max-width: 768px) {
    font-size: 0.85rem;
  }
`;

const Description = styled(motion.div)`
  color: #666;
  line-height: 1.4;
  text-align: left;
  padding: 0.25rem 0;
  margin-top: 0.25rem;
  font-size: 0.85rem;

  @media (max-width: 768px) {
    font-size: 0.8rem;
  }
`;

const ToggleIcon = styled(motion.div)`
  display: flex;
  align-items: center;
  margin-left: 1rem;
`;

const education = [
  {
    year: '2021 - Present',
    school: 'Maharana Pratap Engineering College',
    degree: 'B.Tech in Computer Science',
    description: "Currently pursuing Bachelor's degree in Computer Science with focus on web development and data analysis."
  },
  {
    year: '2020 - 2021',
    school: 'Bishambhar Nath Sanatan Dharam Inter College',
    degree: 'Intermediate',
    description: "Completed intermediate education with focus on science subjects. Developed strong foundation in mathematics and computer science."
  },
  {
    year: '2018 - 2019',
    school: 'St. Francis Xavier Inter College',
    degree: 'High School',
    description: "Completed high school education with honors, reflecting a commitment to academic rigor and outstanding performance across disciplines."
  }
];

const Education = () => {
  const [expandedItems, setExpandedItems] = useState({});

  const toggleItem = (index) => {
    setExpandedItems(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  return (
    <EducationContainer>
      <Content>
        <SubTitle
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          MY JOURNEY
        </SubTitle>
        <Title
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          EDUCATION
        </Title>

        <Timeline>
          {education.map((item, index) => (
            <TimelineItem
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.2 * (index + 2) }}
            >
              <TimelineContent align={index % 2 === 0 ? 'left' : 'right'}>
                <TimelineHeader onClick={() => toggleItem(index)}>
                  <div>
                    <SchoolName>{item.school}</SchoolName>
                    <Year>{item.year}</Year>
                  </div>
                  <ToggleIcon
                    animate={{ rotate: expandedItems[index] ? 180 : 0 }}
                  >
                    <FaChevronDown />
                  </ToggleIcon>
                </TimelineHeader>
                <Degree>{item.degree}</Degree>
                <AnimatePresence>
                  {expandedItems[index] && (
                    <Description
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3 }}
                    >
                      {item.description}
                    </Description>
                  )}
                </AnimatePresence>
              </TimelineContent>
              <TimelineIcon>
                <FaGraduationCap />
              </TimelineIcon>
            </TimelineItem>
          ))}
        </Timeline>
      </Content>
    </EducationContainer>
  );
};

export default Education;